import { useState } from 'react';
import { Info, Warning, CaretDown, CaretRight } from '@phosphor-icons/react';

interface Props {
  /** Concrete reden waarom er nu niet gemaild kan worden; leeg = het kan wel. */
  blockReason?: string | null;
  /** Optionele actie bij de reden, bv. "Ontvangers openen" op de detailpagina. */
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Uitleg bij het mailgedeelte van een renovatieticket: waarom een mail soms niet
 * verstuurd kan worden, en wat je eraan doet.
 *
 * Aanleiding: de verzendknop stond in die gevallen gewoon uit, met de reden enkel
 * in een tooltip. Voor wie de regels niet kent, leek het alsof het mailen stuk was.
 * De concrete reden komt uit {@link renoMailBlockReason}; de regels eronder staan
 * dichtgeklapt, zodat ze niet in de weg staan wie ze al kent.
 */
export function RenoMailInfoNotice({ blockReason, actionLabel, onAction }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-1.5">
      {blockReason && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2">
          <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs text-amber-900">{blockReason}</p>
            {actionLabel && onAction && (
              <button
                type="button"
                onClick={onAction}
                className="text-xs font-medium text-amber-900 underline underline-offset-2 hover:no-underline"
              >
                {actionLabel}
              </button>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 text-xs text-[var(--muted-foreground)] hover:text-neutral-primary"
      >
        {open ? <CaretDown size={11} /> : <CaretRight size={11} />}
        <Info size={12} />
        Wanneer kan een mail niet verstuurd worden?
      </button>

      {open && (
        <ul className="space-y-1 rounded-md border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-xs text-[var(--muted-foreground)]">
          <li>
            <span className="font-medium text-neutral-primary">Geen e-mailadres.</span> We mailen
            enkel naar ontvangers met een gekend adres. Een ontvanger zonder adres staat in het
            oranje en kan je hier meteen aanvullen — dat bewaart het adres ook op de fiche, dus
            de volgende keer staat het er.
          </li>
          <li>
            <span className="font-medium text-neutral-primary">Geen ontvangers gevonden.</span> Bij
            het kanaal <em>huurder</em> zoeken we de huurders van het gebouw en de unit, bij
            <em> leverancier</em> de leveranciers die aan het ticket hangen. Hangt daar niets, dan
            valt er niets te selecteren — zoek dan zelf een contactpersoon of firma.
          </li>
          <li>
            <span className="font-medium text-neutral-primary">Geen gebouw gekozen.</span> Huurders
            en leveranciers worden op het gebouw gezocht; zonder gebouw kunnen we ze niet bepalen.
          </li>
          <li>
            <span className="font-medium text-neutral-primary">Niets aangevinkt.</span> Ook met
            geldige adressen vertrekt er niets tot je minstens één ontvanger aanvinkt.
          </li>
        </ul>
      )}
    </div>
  );
}
